import { useEffect, useState } from 'react';
import { Modal } from './Modal';

export interface DocumentPreviewTarget {
  /** Heading for the dialog — whose document this is. */
  title: string;

  /** The original filename, used for the download and as the frame's title. */
  fileName: string;

  /** Fetches the bytes. Supplied by the caller so each side uses the route its own group can reach. */
  load: (signal: AbortSignal) => Promise<{ blob: Blob }>;
}

interface DocumentPreviewModalProps {
  target: DocumentPreviewTarget;
  onClose: () => void;
}

/**
 * Shows a supporting document without leaving the page.
 *
 * The file is fetched as a blob rather than pointed at with a URL, because the endpoint serving it
 * needs the same bearer token as every other call and neither an &lt;iframe src&gt; nor an
 * &lt;a href&gt; can carry one. The blob URL that comes back is revoked when the dialog closes — not
 * straight after use, which would cancel the download it was created for.
 */
export function DocumentPreviewModal({ target, onClose }: DocumentPreviewModalProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const { load, fileName, title } = target;

  useEffect(() => {
    const controller = new AbortController();
    let created: string | null = null;

    (async () => {
      try {
        const { blob } = await load(controller.signal);
        created = URL.createObjectURL(blob);
        setContentType(blob.type);
        setObjectUrl(created);
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Could not open the document.');
        }
      }
    })();

    return () => {
      controller.abort();
      if (created) {
        URL.revokeObjectURL(created);
      }
      setObjectUrl(null);
      setContentType('');
      setError(null);
    };
  }, [load]);

  const isImage = contentType.startsWith('image/');

  return (
    <Modal
      open
      wide
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button button--quiet" onClick={onClose}>
            Close
          </button>
          {objectUrl && (
            <a className="button button--primary" href={objectUrl} download={fileName}>
              Download
            </a>
          )}
        </>
      }
    >
      {error && (
        <div className="issues__block issues__block--error">
          <p>{error}</p>
        </div>
      )}

      {!error && !objectUrl && <p className="care-empty">Opening…</p>}

      {objectUrl &&
        (isImage ? (
          <img className="care-preview__image" src={objectUrl} alt={fileName} />
        ) : (
          <iframe className="care-preview__frame" src={objectUrl} title={fileName} />
        ))}
    </Modal>
  );
}
